import type { SponsoredBrandAsset, SponsoredDisplayCapabilities } from "@adpilot/shared";

export interface Crop {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Largest centered crop of the asset that matches the requested width/height ratio. */
export function cropFor(asset: SponsoredBrandAsset, ratio: number): Crop {
  const assetRatio = asset.width / asset.height;
  if (assetRatio > ratio) {
    const width = Math.max(1, Math.round(asset.height * ratio));
    return { top: 0, left: Math.round((asset.width - width) / 2), width, height: asset.height };
  }
  const height = Math.max(1, Math.round(asset.width / ratio));
  return { top: Math.round((asset.height - height) / 2), left: 0, width: asset.width, height };
}

/**
 * Splits the asset library into the logos and custom images Amazon accepts for an SD
 * creative: active, unrejected images that meet each slot's size and format rules.
 */
export function selectCreativeAssets(
  assets: SponsoredBrandAsset[] | undefined,
  limits: Pick<SponsoredDisplayCapabilities["limits"], "logoMaxBytes" | "imageMaxBytes">,
): { logoAssets: SponsoredBrandAsset[]; imageAssets: SponsoredBrandAsset[] } {
  const usableImages = (assets ?? []).filter(
    (asset) =>
      asset.type === "IMAGE" &&
      asset.status === "ACTIVE" &&
      !Object.values(asset.moderationPolicies).includes("REJECTED"),
  );
  const logoAssets = usableImages.filter(
    (asset) =>
      asset.width >= 600 &&
      asset.height >= 100 &&
      asset.sizeBytes <= limits.logoMaxBytes &&
      ["image/png", "image/jpeg", "image/jpg"].includes(asset.contentType?.toLowerCase() ?? ""),
  );
  const imageAssets = usableImages.filter(
    (asset) =>
      asset.width >= 1200 &&
      asset.height >= 628 &&
      asset.sizeBytes <= limits.imageMaxBytes &&
      ["image/png", "image/jpeg", "image/jpg", "image/gif"].includes(
        asset.contentType?.toLowerCase() ?? "",
      ),
  );
  return { logoAssets, imageAssets };
}
